/**
 * Benjamin Moore Color Picker — Shopify Asset
 * File: assets/bm-color-picker.js
 *
 * MULTI-INSTANCE SAFE VERSION
 * Supports more than one .bm-picker-wrap on the same page (e.g. main
 * product form + a quick-view modal). All DOM lookups are scoped to the
 * specific .bm-picker-wrap being initialized instead of relying on
 * document-wide getElementById calls, which only ever match the first
 * instance in the DOM.
 *
 * Features:
 *  • Curated "Popular Colors" default view (top 150 best-sellers) for fast open
 *  • Full BM color database (~3,900+ colors) loaded on demand — search, family
 *    filter, or "Browse full colour library" all trigger a lazy fetch of the
 *    full catalog the first time it's needed (shared across all instances)
 *  • Search by name or color code
 *  • Filter by color family
 *  • Hover tooltip with color preview
 *  • Click to select → updates hidden input + displayed swatch
 *  • Validates color is selected before Add to Cart
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // iOS Safari fix: elements with a mouseenter/mouseover listener require
  // TWO taps to fire click (first tap only simulates hover) unless the
  // document has a touchstart listener bound somewhere. This no-op
  // listener alone resolves it for the whole page.
  // ---------------------------------------------------------------------
  document.addEventListener('touchstart', function () {}, { passive: true });

  var isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

  // ---------------------------------------------------------------------
  // Shared, page-wide state: the color catalog itself. This is safe to
  // share across every picker instance — it's just data.
  // ---------------------------------------------------------------------
  const BM_COLORS_POPULAR = window.BM_COLORS_POPULAR || [];
  let BM_COLORS = [];
  let _fullCatalogLoaded = false;
  let _fullCatalogLoading = false;
  let _fullCatalogCallbacks = [];

  function ensureFullCatalog(callback) {
    if (_fullCatalogLoaded) {
      callback();
      return;
    }
    _fullCatalogCallbacks.push(callback);
    if (_fullCatalogLoading) return;
    _fullCatalogLoading = true;

    const urls = window.BM_COLOR_PART_URLS || [];

    function finishLoadingFullCatalog() {
      BM_COLORS = [].concat(
        window.BM_COLORS_PART_1 || [],
        window.BM_COLORS_PART_2 || [],
        window.BM_COLORS_PART_3 || [],
        window.BM_COLORS_PART_4 || [],
        window.BM_COLORS_PART_5 || [],
        window.BM_COLORS_PART_6 || [],
        window.BM_COLORS_PART_7 || [],
        window.BM_COLORS_PART_8 || []
      );
      _fullCatalogLoaded = true;
      _fullCatalogLoading = false;
      const callbacks = _fullCatalogCallbacks;
      _fullCatalogCallbacks = [];
      callbacks.forEach((cb) => cb());
    }

    if (!urls.length) {
      finishLoadingFullCatalog();
      return;
    }

    let remaining = urls.length;
    urls.forEach((url) => {
      const s = document.createElement('script');
      s.src = url;
      s.onload = onPartLoaded;
      s.onerror = onPartLoaded;
      document.body.appendChild(s);
    });

    function onPartLoaded() {
      remaining -= 1;
      if (remaining === 0) finishLoadingFullCatalog();
    }
  }

  function getLuminance(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  }

  // ---------------------------------------------------------------------
  // Per-instance init. `wrap` is a single .bm-picker-wrap container.
  // Every element is looked up via wrap.querySelector(...) so two wraps
  // on the same page never see or touch each other's DOM.
  // ---------------------------------------------------------------------
  function initInstance(wrap) {
    if (wrap.dataset.bmInited === 'true') return;
    wrap.dataset.bmInited = 'true';

    const overlay       = wrap.querySelector('.bm-modal-overlay');
    const triggerBtn     = wrap.querySelector('.bm-selected-display');
    const closeBtn       = wrap.querySelector('.bm-close-btn');
    const searchInput    = wrap.querySelector('.bm-search-input');
    const searchClear    = wrap.querySelector('.bm-search-clear');
    const grid           = wrap.querySelector('.bm-color-grid');
    const familyPills    = wrap.querySelectorAll('.bm-family-pill');
    const tooltip        = wrap.querySelector('.bm-tooltip');
    const tooltipSelect  = wrap.querySelector('.bm-tooltip-select');
    const hiddenInput    = wrap.querySelector('.bm-selected-color-input, #bm-selected-color-input');
    const browseFullBtn  = wrap.querySelector('.bm-browse-full-link');
    const noResults       = wrap.querySelector('.bm-no-results');
    const countEl         = wrap.querySelector('.bm-results-count');
    const loadingEl       = wrap.querySelector('.bm-loading');
    const tooltipSwatch   = wrap.querySelector('.bm-tooltip-swatch');
    const tooltipNameEl   = wrap.querySelector('.bm-tooltip-name');
    const tooltipCodeEl   = wrap.querySelector('.bm-tooltip-code');
    const nameEl          = wrap.querySelector('.bm-selected-name');
    const iconEl          = wrap.querySelector('.bm-swatch-icon');

    if (!overlay || !triggerBtn) return;

    // Per-instance state (was module-level globals before — that's what
    // let one instance's modal control another's).
    let _selectedColor = null;
    let _activeFamily  = 'all';
    let _searchQuery   = '';
    let _browseFullCatalog = false;
    let _tooltipTarget = null;
    let _tooltipTimer  = null;

    triggerBtn.addEventListener('click', () => {
      overlay.hidden = false;
      triggerBtn.setAttribute('aria-expanded', 'true');
      searchInput.focus();
      _browseFullCatalog = false;
      renderGrid();
    });

    const closeModal = () => {
      overlay.hidden = true;
      triggerBtn.setAttribute('aria-expanded', 'false');
      hideTooltip();
    };

    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !overlay.hidden) closeModal();
    });

    searchInput.addEventListener('input', () => {
      _searchQuery = searchInput.value.trim().toLowerCase();
      searchClear.hidden = _searchQuery.length === 0;
      renderGrid();
    });
    searchClear.addEventListener('click', () => {
      searchInput.value = '';
      _searchQuery = '';
      searchClear.hidden = true;
      searchInput.focus();
      renderGrid();
    });

    familyPills.forEach((pill) => {
      pill.addEventListener('click', () => {
        familyPills.forEach((p) => p.classList.remove('active'));
        pill.classList.add('active');
        _activeFamily = pill.dataset.family;
        renderGrid();
      });
    });

    if (browseFullBtn) {
      browseFullBtn.addEventListener('click', () => {
        _browseFullCatalog = true;
        renderGrid();
      });
    }

    tooltipSelect.addEventListener('click', () => {
      if (_tooltipTarget) {
        selectColor(_tooltipTarget);
        hideTooltip();
        closeModal();
      }
    });

    const form = triggerBtn.closest('form') || wrap.closest('form') || document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('submit', (e) => {
        if (!_selectedColor) {
          e.preventDefault();
          triggerBtn.style.borderColor = '#c0392b';
          triggerBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          alert('Please select a paint color before adding to cart.');
          return false;
        }
      });
    }

    function usingCuratedView() {
      return _activeFamily === 'all' && !_searchQuery && !_browseFullCatalog;
    }

    function getFilteredColors() {
      if (usingCuratedView()) {
        return BM_COLORS_POPULAR;
      }
      return BM_COLORS.filter((c) => {
        const [name, code, , family] = c;
        const matchesFamily = _activeFamily === 'all' || family === _activeFamily;
        const matchesSearch = !_searchQuery ||
          name.toLowerCase().includes(_searchQuery) ||
          code.toLowerCase().includes(_searchQuery);
        return matchesFamily && matchesSearch;
      });
    }

    function renderGrid() {
      if (_activeFamily === 'custom') {
        if (loadingEl) loadingEl.hidden = true;
        noResults.hidden = true;
        countEl.textContent = 'Custom Color';

        grid.innerHTML = `
          <div class="bm-custom-color-wrap">
            <div class="bm-custom-preview">
              <div class="bm-preview-info" style="width:100%;">
                <label>Color Name or Color Code</label>
                <input
                  type="text"
                  class="bm-custom-name"
                  placeholder="Red or #C62036"
                >
              </div>
            </div>

            <button
              type="button"
              class="bm-custom-select">
              ✓ Use this Color
            </button>
          </div>
        `;

        const input = grid.querySelector('.bm-custom-name');

        grid.querySelector('.bm-custom-select').addEventListener('click', () => {
          const value = input.value.trim();

          if (!value) {
            input.focus();
            return;
          }

          const color = {
            name: value,
            code: '',
            hex: '.'
          };

          selectColor(color);
          closeModal();
        });

        if (browseFullBtn) browseFullBtn.hidden = true;
        return;
      }

      if (!usingCuratedView() && !_fullCatalogLoaded) {
        grid.innerHTML = '';
        noResults.hidden = true;
        if (browseFullBtn) browseFullBtn.hidden = true;
        countEl.textContent = '';
        if (loadingEl) loadingEl.hidden = false;
        ensureFullCatalog(() => {
          renderGrid();
        });
        return;
      }
      if (loadingEl) loadingEl.hidden = true;

      const colors = getFilteredColors();

      if (browseFullBtn) browseFullBtn.hidden = !usingCuratedView();

      countEl.textContent = usingCuratedView()
        ? colors.length + ' Popular Colors'
        : colors.length + ' Results';

      if (colors.length === 0) {
        grid.innerHTML = '';
        noResults.hidden = false;
        return;
      }
      noResults.hidden = true;

      grid.innerHTML = colors.map(([name, code, hex]) => {
        const luminance  = getLuminance(hex);
        const lightText  = luminance < 0.35 ? 'light-text' : '';
        const isSelected = _selectedColor && _selectedColor.code === code;
        return `<div
          class="bm-color-cell${lightText ? ' ' + lightText : ''}${isSelected ? ' selected' : ''}"
          style="background:${hex}"
          data-name="${name.replace(/"/g, '&quot;')}"
          data-code="${code}"
          data-hex="${hex}"
          role="option"
          aria-selected="${isSelected}"
          tabindex="0"
          title="${name} ${code}"
        >${name}</div>`;
      }).join('');

      grid.querySelectorAll('.bm-color-cell').forEach((cell) => {
        if (!isTouchDevice) {
          cell.addEventListener('mouseenter', (e) => showTooltip(cell, e));
          cell.addEventListener('mouseleave', () => scheduleHideTooltip());
        }
        cell.addEventListener('click', () => {
          const color = { name: cell.dataset.name, code: cell.dataset.code, hex: cell.dataset.hex };
          selectColor(color);
          closeModal();
        });
        cell.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const color = { name: cell.dataset.name, code: cell.dataset.code, hex: cell.dataset.hex };
            selectColor(color);
            closeModal();
          }
        });
      });

      if (!isTouchDevice) {
        tooltip.addEventListener('mouseenter', () => clearTimeout(_tooltipTimer));
        tooltip.addEventListener('mouseleave', () => scheduleHideTooltip());
      }
    }

    function showTooltip(cell) {
      clearTimeout(_tooltipTimer);
      _tooltipTarget = { name: cell.dataset.name, code: cell.dataset.code, hex: cell.dataset.hex };

      tooltipSwatch.style.background = cell.dataset.hex;
      tooltipNameEl.textContent = cell.dataset.name;
      tooltipCodeEl.textContent = cell.dataset.code;
      tooltip.hidden = false;

      const cellRect  = cell.getBoundingClientRect();
      const gridRect  = grid.getBoundingClientRect();
      const tipWidth  = 230;
      const tipHeight = 80;

      let left = cellRect.right - gridRect.left + 8;
      let top  = cellRect.top  - gridRect.top;

      if (left + tipWidth > gridRect.width) {
        left = cellRect.left - gridRect.left - tipWidth - 8;
      }
      if (top + tipHeight > gridRect.height) {
        top = gridRect.height - tipHeight - 8;
      }

      tooltip.style.left = Math.max(0, left) + 'px';
      tooltip.style.top  = Math.max(0, top)  + 'px';
    }

    function scheduleHideTooltip() {
      _tooltipTimer = setTimeout(hideTooltip, 200);
    }

    function hideTooltip() {
      tooltip.hidden = true;
      _tooltipTarget = null;
    }

    function selectColor(color) {
      _selectedColor = color;

      triggerBtn.style.background   = color.hex;
      triggerBtn.style.borderColor  = 'transparent';
      triggerBtn.classList.add('has-color');

      const luminance = getLuminance(color.hex === '.' ? '#ffffff' : color.hex);
      const textColor = luminance < 0.35 ? '#fff' : '#1a1a1a';
      triggerBtn.style.color = textColor;

      nameEl.textContent = color.code ? `${color.name} ${color.code}` : color.name;

      iconEl.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C62036" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M14 4l6 6-8 8H6v-6z"></path>
  <path d="M6 18c-1.5 0-3 1-3 2.5S4.5 23 6 23s3-1 3-2.5S7.5 18 6 18z"></path>
</svg>`;

      hiddenInput.value = color.code ? `${color.name} (${color.code})` : color.name;
    }
  }

  // ---------------------------------------------------------------------
  // Boot: initialize every picker wrap currently on the page. Called on
  // DOMContentLoaded, on Shopify section reloads, and can safely be
  // re-run any time (e.g. after a quick-view modal injects new markup)
  // since already-inited wraps are skipped via data-bm-inited.
  // ---------------------------------------------------------------------
  function initAll() {
    document.querySelectorAll('.bm-picker-wrap').forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  // Cart drawers, quick-view modals, and similar UI are usually rendered
  // via AJAX *after* the initial page load, so a one-time scan on
  // DOMContentLoaded misses them. Watch the whole page for any new
  // .bm-picker-wrap being inserted and initialize it the moment it
  // appears, regardless of where or how it was injected.
  if (window.MutationObserver) {
    var bmObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return; // element nodes only
          if (node.matches && node.matches('.bm-picker-wrap')) {
            initInstance(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.bm-picker-wrap').forEach(initInstance);
          }
        });
      });
    });
    bmObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  // If your quick-view is injected dynamically after an AJAX fetch,
  // call window.BMColorPicker.initAll() right after you insert that
  // markup into the DOM (see loader script notes below).
  window.BMColorPicker = { initAll: initAll };

})();